import { useEffect, useState } from "react";

// Decode audio once via Web Audio, downsample to a fixed peak resolution,
// and cache by URL so re-renders + multiple visible players don't redecode.
// Bars in the DOM are then a cheap resample of this peaks array.
//
// When the backend has already attached pre-computed peaks to a row
// (Reference.peaks, Reference.voicePreviewPeaks, Take.peaks), the UI dequantises
// them synchronously and skips the audio download entirely. The decode path
// still exists as a fallback for legacy rows and lazily backfills via
// POST /audio/peaks so subsequent loads hit the fast path.

export const PEAK_RESOLUTION = 480;
// Cache key strips the presigned-URL query string so repeated reloads (each
// of which mints a new signature for the same S3 object) collapse to one
// entry per asset. Without this, the cache grows unboundedly because every
// `listReferences` poll produces fresh URLs for unchanged audio.
function cacheKey(url: string): string {
  const qIdx = url.indexOf("?");
  return qIdx >= 0 ? url.slice(0, qIdx) : url;
}
const peaksCache = new Map<string, Float32Array>();
const peaksFailed = new Set<string>();
const peaksInflight = new Map<string, Promise<Float32Array>>();
let sharedAudioCtx: AudioContext | null = null;

function getSharedAudioCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!sharedAudioCtx || sharedAudioCtx.state === "closed") {
    sharedAudioCtx = new Ctor();
  }
  return sharedAudioCtx;
}

// Quantise normalised peaks (0..1) → uint8 (0..255) → base64. The 480-byte
// payload is what the backend stores on Reference/Take.
export function encodePeaksBase64(peaks: Float32Array): string {
  const bytes = new Uint8Array(peaks.length);
  for (let i = 0; i < peaks.length; i++) {
    const v = peaks[i] || 0;
    bytes[i] = Math.max(0, Math.min(255, Math.round(v * 255)));
  }
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

export function decodePeaksBase64(b64: string): Float32Array | null {
  try {
    const binary = atob(b64);
    const out = new Float32Array(binary.length);
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i) / 255;
    return out;
  } catch {
    return null;
  }
}

export async function loadAudioPeaks(url: string): Promise<Float32Array> {
  const key = cacheKey(url);
  const cached = peaksCache.get(key);
  if (cached) return cached;
  const inflight = peaksInflight.get(key);
  if (inflight) return inflight;
  const promise = (async () => {
    const ctx = getSharedAudioCtx();
    if (!ctx) throw new Error("AudioContext unavailable");
    const res = await fetch(url);
    if (!res.ok) throw new Error(`fetch ${res.status}`);
    const buf = await res.arrayBuffer();
    const decoded = await ctx.decodeAudioData(buf);
    const channel = decoded.getChannelData(0);
    const samples = PEAK_RESOLUTION;
    const blockSize = Math.max(1, Math.floor(channel.length / samples));
    const out = new Float32Array(samples);
    let max = 0;
    for (let i = 0; i < samples; i++) {
      const base = i * blockSize;
      let peak = 0;
      for (let j = 0; j < blockSize; j++) {
        const v = Math.abs(channel[base + j] || 0);
        if (v > peak) peak = v;
      }
      out[i] = peak;
      if (peak > max) max = peak;
    }
    if (max > 0) {
      for (let i = 0; i < samples; i++) out[i] /= max;
    }
    peaksCache.set(key, out);
    return out;
  })();
  peaksInflight.set(key, promise);
  promise.catch(() => peaksFailed.add(key)).finally(() => peaksInflight.delete(key));
  return promise;
}

// Descriptor passed when a caller wants the decode-fallback path to also
// fire-and-forget POST /audio/peaks so the row gains peaks for next time.
// `onBackfill` is supplied by api.ts so this module doesn't import the
// API client (keeps it a leaf util).
export interface PeaksBackfill {
  onBackfill: (peaks: string) => void;
}

export function useAudioPeaks(
  url: string | undefined,
  prePeaks?: string | null,
  backfill?: PeaksBackfill,
): Float32Array | null {
  const initialPre = prePeaks ? decodePeaksBase64(prePeaks) : null;
  const initialCached = url ? peaksCache.get(cacheKey(url)) ?? null : null;
  const [peaks, setPeaks] = useState<Float32Array | null>(initialPre ?? initialCached);

  useEffect(() => {
    // Pre-computed peaks from the server — synchronous, no fetch. This is the
    // common path once backfill catches up.
    if (prePeaks) {
      const decoded = decodePeaksBase64(prePeaks);
      if (decoded) {
        if (url) peaksCache.set(cacheKey(url), decoded);
        setPeaks(decoded);
        return;
      }
    }
    if (!url) {
      setPeaks(null);
      return;
    }
    const key = cacheKey(url);
    const cached = peaksCache.get(key);
    if (cached) {
      setPeaks(cached);
      return;
    }
    if (peaksFailed.has(key)) {
      setPeaks(null);
      return;
    }
    let cancelled = false;
    loadAudioPeaks(url)
      .then((p) => {
        if (cancelled) return;
        setPeaks(p);
        if (backfill) {
          try {
            backfill.onBackfill(encodePeaksBase64(p));
          } catch {
            // Backfill is best-effort — never let it surface to the UI.
          }
        }
      })
      .catch(() => {
        if (!cancelled) setPeaks(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url, prePeaks, backfill]);
  return peaks;
}

// Map normalized peaks (0..1) to bar heights matching `.take-wave-layer i`
// (3..27 px range).
export function peaksToBars(peaks: Float32Array, count: number): number[] {
  const out: number[] = new Array(count);
  const ratio = peaks.length / count;
  for (let i = 0; i < count; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.max(start + 1, Math.floor((i + 1) * ratio));
    let peak = 0;
    for (let j = start; j < end && j < peaks.length; j++) {
      if (peaks[j] > peak) peak = peaks[j];
    }
    out[i] = 3 + Math.max(peak, 0.04) * 24;
  }
  return out;
}

// Synthetic fallback waveform — used while real peaks are loading or when
// decoding fails. Stable per seed so it doesn't reshuffle on re-render.
export function waveformBars(seed: number, count: number): number[] {
  const stepScale = 36 / Math.max(1, count);
  return Array.from({ length: count }, (_, i) =>
    3 + Math.abs(Math.sin(i * stepScale + (seed + 1) * 1.5)) * 24
  );
}

export const WAVEFORM_PX_PER_BAR = 3;
export const WAVEFORM_MIN_BARS = 24;
export const WAVEFORM_MAX_BARS = 320;
